import { useState } from "react";
import { Link } from "@shared/client/navigation";
import { Check, KeyRound, LoaderCircle } from "lucide-react";
import { updateProfile } from "@shared/lib/auth-client";
import { joinName, nameParts, type UserMetadata } from "@shared/lib/auth-shared";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { useT } from "@shared/client/locale";

type Profile = { first_name: string; last_name: string; company: string; role: string; website: string };

/** Le profil du compte : un formulaire, un enregistrement. */
export function ProfileForm({ email, metadata }: { email: string; metadata: UserMetadata }) {
  const copy = useT().auth.account;
  const { first, last } = nameParts(metadata);
  const [values, setValues] = useState<Profile>({
    first_name: first,
    last_name: last,
    company: metadata.company || "",
    role: metadata.role || "",
    website: metadata.website || "",
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const trimmed = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()])) as Profile;
    const error = await updateProfile({ ...trimmed, full_name: joinName(trimmed.first_name, trimmed.last_name) }).then(() => null, (cause: Error) => cause);
    setBusy(false);
    setMessage(error ? { ok: false, text: error.message } : { ok: true, text: copy.saved });
  }

  function field(key: keyof Profile, label: string, props: React.ComponentProps<typeof Input> = {}) {
    return (
      <div className="grid gap-1.5">
        <Label htmlFor={`account-${key}`}>{label}</Label>
        <Input
          id={`account-${key}`}
          onChange={(event) => setValues({ ...values, [key]: event.target.value })}
          value={values[key]}
          {...props}
        />
      </div>
    );
  }

  return (
    <form className="grid gap-4 sm:grid-cols-2" onSubmit={save}>
      {field("first_name", copy.firstName, { required: true, autoComplete: "given-name" })}
      {field("last_name", copy.lastName, { required: true, autoComplete: "family-name" })}
      <div className="grid gap-1.5">
        <Label htmlFor="account-email">{copy.email}</Label>
        <Input disabled id="account-email" value={email} />
      </div>
      {field("company", copy.company, { autoComplete: "organization" })}
      <div className="sm:col-span-2">{field("role", copy.role, { autoComplete: "organization-title" })}</div>
      <div className="sm:col-span-2">
        {field("website", copy.website, { autoComplete: "url", inputMode: "url", placeholder: "https://" })}
      </div>
      <div className="flex items-center gap-3 sm:col-span-2">
        <Button disabled={busy} type="submit" variant="outline">
          {busy && <LoaderCircle className="animate-spin" />} {copy.save}
        </Button>
        {message && (
          <p className={`m-0 flex items-center gap-1.5 text-[12.5px] font-medium ${message.ok ? "text-success" : "text-destructive"}`}>
            {message.ok && <Check className="size-4" aria-hidden="true" />}
            {message.text}
          </p>
        )}
      </div>
    </form>
  );
}

export function PasswordButton() {
  const copy = useT().auth.account;
  return (
    <Button asChild variant="outline">
      <Link href="/auth/forgot-password">
        <KeyRound /> {copy.changePassword}
      </Link>
    </Button>
  );
}
